import type { IdentityState } from '@/store/features/plaid/identitySlice';
import {
  EMPLOYMENT_STATUSES,
  ISO_3166_1_ALPHA_2_CODES,
  MARITAL_STATUS,
  type Address,
  type ApexUserInformation,
  type CountryType,
  type NameSuffixType,
  USD_RANGE,
  type UsdRangeType,
} from '@/util/account-openning-util';
export { mapVerificationResultToKycResult } from '@/pages/kyc/verification-result';

type ApexKycPrefill = Pick<
  ApexUserInformation,
  'firstName' | 'lastName' | 'dateOfBirth' | 'personalAddress' | 'phoneNumber'
>;

type ApexOnboardingAddressPrefill = {
  line1?: unknown;
  line2?: unknown;
  line3?: unknown;
  city?: unknown;
  state?: unknown;
  postalCode?: unknown;
  country?: unknown;
};

export type ApexOnboardingPrefill = {
  firstName?: unknown;
  middleName?: unknown;
  lastName?: unknown;
  suffix?: unknown;
  dateOfBirth?: unknown;
  countryOfCitizenship?: unknown;
  countryOfCitizenship2?: unknown;
  personalAddress?: ApexOnboardingAddressPrefill;
  phoneNumber?: unknown;
  employmentStatus?: unknown;
  occupation?: unknown;
  employer?: unknown;
  startYear?: unknown;
  annualIncomeRangeUsd?: unknown;
  totalNetWorthRangeUsd?: unknown;
  maritalStatus?: unknown;
  isAdvisor?: unknown;
  isInstitutionalCustomer?: unknown;
  isCorrespondentEmployee?: unknown;
  isFinraAffiliated?: unknown;
  isControlPerson?: unknown;
  hasPoliticallyExposedFamily?: unknown;
  hasPoliticallyExposedOrganization?: unknown;
  controlPersonCompanySymbols?: unknown;
  finraAssociatedEntity?: unknown;
  politicallyExposedFamilyNames?: unknown;
  politicallyExposedOrganization?: unknown;
};

const NAME_SUFFIXES = [
  'NAME_SUFFIX_UNSPECIFIED',
  'SR',
  'JR',
  'III',
  'IV',
  'V',
] as const satisfies readonly NameSuffixType[];

const trimToValue = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

const hasValue = (value: unknown): value is string =>
  typeof value === 'string' && trimToValue(value) !== undefined;

const toOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' ? trimToValue(value) : undefined;

const enumValue = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined => {
  const normalized = toOptionalString(value);
  return normalized && allowed.includes(normalized as T) ? (normalized as T) : undefined;
};

const toDate = (value: unknown): Date | undefined => {
  const normalized = toOptionalString(value);
  if (!normalized || !/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return undefined;

  const parsed = new Date(`${normalized}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== normalized
    ? undefined
    : parsed;
};

const toCountry = (value: unknown): CountryType | undefined =>
  enumValue(value, ISO_3166_1_ALPHA_2_CODES);

const toAddress = (
  existing: Address | undefined,
  source: ApexOnboardingAddressPrefill | undefined,
): Address | undefined => {
  if (
    !source ||
    ![
      source.line1,
      source.line2,
      source.line3,
      source.city,
      source.state,
      source.postalCode,
      source.country,
    ].some(hasValue)
  ) {
    return undefined;
  }

  const country = toCountry(source.country) ?? existing?.country ?? 'US';
  return {
    line1: hasValue(existing?.line1) ? existing.line1 : (toOptionalString(source.line1) ?? ''),
    line2: hasValue(existing?.line2) ? existing.line2 : (toOptionalString(source.line2) ?? ''),
    line3: hasValue(existing?.line3) ? existing.line3 : (toOptionalString(source.line3) ?? ''),
    city: hasValue(existing?.city) ? existing.city : (toOptionalString(source.city) ?? ''),
    state: hasValue(existing?.state) ? existing.state : (toOptionalString(source.state) ?? ''),
    postalCode: hasValue(existing?.postalCode)
      ? existing.postalCode
      : (toOptionalString(source.postalCode) ?? ''),
    country,
  };
};

/**
 * Map only approved firm-data profile fields into the Apex onboarding model.
 * Sensitive fields such as SSN are intentionally not represented here.
 */
export function getMissingApexPersonalInfoFromFirmData(
  userInfo: ApexUserInformation,
  prefill: ApexOnboardingPrefill | null | undefined,
): Partial<ApexUserInformation> {
  if (!prefill) return {};

  const missing: Partial<ApexUserInformation> = {};

  if (!hasValue(userInfo.firstName)) {
    const firstName = toOptionalString(prefill.firstName);
    if (firstName) missing.firstName = firstName;
  }
  if (!hasValue(userInfo.middleName)) {
    const middleName = toOptionalString(prefill.middleName);
    if (middleName) missing.middleName = middleName;
  }
  if (!hasValue(userInfo.lastName)) {
    const lastName = toOptionalString(prefill.lastName);
    if (lastName) missing.lastName = lastName;
  }
  if (!userInfo.suffix) {
    const suffix = enumValue(prefill.suffix, NAME_SUFFIXES);
    if (suffix) missing.suffix = suffix;
  }

  if (!userInfo.dateOfBirth) {
    const dateOfBirth = toDate(prefill.dateOfBirth);
    if (dateOfBirth) missing.dateOfBirth = dateOfBirth;
  }
  if (!hasValue(userInfo.phoneNumber)) {
    const phoneNumber = toOptionalString(prefill.phoneNumber);
    if (phoneNumber) missing.phoneNumber = phoneNumber;
  }
  if (!userInfo.countryOfCitizenship) {
    const citizenship = toCountry(prefill.countryOfCitizenship);
    if (citizenship) missing.countryOfCitizenship = citizenship;
  }
  if (!userInfo.countryOfCitizenship2) {
    const citizenship = toCountry(prefill.countryOfCitizenship2);
    if (citizenship) missing.countryOfCitizenship2 = citizenship;
  }
  if (!userInfo.maritalStatus) {
    const maritalStatus = enumValue(prefill.maritalStatus, MARITAL_STATUS);
    if (maritalStatus) missing.maritalStatus = maritalStatus;
  }

  const address = toAddress(userInfo.personalAddress, prefill.personalAddress);
  if (address) missing.personalAddress = address;

  {
    if (!userInfo.employmentStatus) {
      const employmentStatus = enumValue(prefill.employmentStatus, EMPLOYMENT_STATUSES);
      if (employmentStatus) missing.employmentStatus = employmentStatus;
    }
    if (!hasValue(userInfo.occupation)) {
      const occupation = toOptionalString(prefill.occupation);
      if (occupation) missing.occupation = occupation;
    }
    if (!hasValue(userInfo.employer)) {
      const employer = toOptionalString(prefill.employer);
      if (employer) missing.employer = employer;
    }
    if (!hasValue(userInfo.startYear)) {
      const startYear = toOptionalString(prefill.startYear);
      if (startYear && /^\d{4}$/.test(startYear)) missing.startYear = startYear;
    }
  }

  const optionalFields: Array<
    | 'annualIncomeRangeUsd'
    | 'totalNetWorthRangeUsd'
    | 'isAdvisor'
    | 'isInstitutionalCustomer'
    | 'isCorrespondentEmployee'
    | 'isFinraAffiliated'
    | 'isControlPerson'
    | 'hasPoliticallyExposedFamily'
    | 'hasPoliticallyExposedOrganization'
    | 'finraAssociatedEntity'
    | 'politicallyExposedFamilyNames'
    | 'politicallyExposedOrganization'
  > = [
    'annualIncomeRangeUsd',
    'totalNetWorthRangeUsd',
    'isAdvisor',
    'isInstitutionalCustomer',
    'isCorrespondentEmployee',
    'isFinraAffiliated',
    'isControlPerson',
    'hasPoliticallyExposedFamily',
    'hasPoliticallyExposedOrganization',
    'finraAssociatedEntity',
    'politicallyExposedFamilyNames',
    'politicallyExposedOrganization',
  ];
  for (const field of optionalFields) {
    if (userInfo[field] !== undefined) continue;
    const value = prefill[field];
    if (
      field === 'finraAssociatedEntity' &&
      (userInfo.isFinraAffiliated === false || prefill.isFinraAffiliated === false)
    ) {
      continue;
    }
    if (
      field === 'politicallyExposedFamilyNames' &&
      (userInfo.hasPoliticallyExposedFamily === false ||
        prefill.hasPoliticallyExposedFamily === false)
    ) {
      continue;
    }
    if (
      field === 'politicallyExposedOrganization' &&
      (userInfo.hasPoliticallyExposedOrganization === false ||
        prefill.hasPoliticallyExposedOrganization === false)
    ) {
      continue;
    }
    if (field === 'annualIncomeRangeUsd' || field === 'totalNetWorthRangeUsd') {
      const range = enumValue(value, USD_RANGE);
      if (range) missing[field] = range as UsdRangeType;
    } else if (typeof value === 'boolean' || hasValue(value)) {
      missing[field] = value as never;
    }
  }

  if (
    !userInfo.controlPersonCompanySymbols?.length &&
    userInfo.isControlPerson !== false &&
    prefill.isControlPerson !== false
  ) {
    const symbols = Array.isArray(prefill.controlPersonCompanySymbols)
      ? prefill.controlPersonCompanySymbols.filter((value): value is string => hasValue(value))
      : [];
    if (symbols.length > 0) missing.controlPersonCompanySymbols = symbols;
  }

  return missing;
}

function hasCompleteAddress(address: ApexUserInformation['personalAddress']): boolean {
  const postalCode = trimToValue(address?.postalCode);

  return (
    address !== undefined &&
    hasValue(address.line1) &&
    hasValue(address.city) &&
    hasValue(address.state) &&
    postalCode?.length === 5
  );
}

export function getMissingApexPersonalInfoFromKyc(
  userInfo: ApexUserInformation,
  kycResult: IdentityState,
): ApexKycPrefill {
  const prefill: ApexKycPrefill = {};

  if (
    hasValue(kycResult.firstName) &&
    hasValue(kycResult.lastName) &&
    !hasValue(userInfo.firstName) &&
    !hasValue(userInfo.lastName)
  ) {
    prefill.firstName = kycResult.firstName;
    prefill.lastName = kycResult.lastName;
  }

  if (kycResult.dateOfBirth && !userInfo.dateOfBirth) {
    prefill.dateOfBirth = kycResult.dateOfBirth;
  }

  if (kycResult.personalAddress && !hasCompleteAddress(userInfo.personalAddress)) {
    const existingAddress = userInfo.personalAddress;
    const existingPostalCode = trimToValue(existingAddress?.postalCode);
    const completeExistingPostalCode =
      existingPostalCode?.length === 5 ? existingPostalCode : undefined;

    prefill.personalAddress = {
      line1: hasValue(existingAddress?.line1)
        ? existingAddress.line1
        : kycResult.personalAddress.line1,
      line2: hasValue(existingAddress?.line2)
        ? existingAddress.line2
        : kycResult.personalAddress.line2 || '',
      line3: hasValue(existingAddress?.line3) ? existingAddress.line3 : '',
      city: hasValue(existingAddress?.city) ? existingAddress.city : kycResult.personalAddress.city,
      state: hasValue(existingAddress?.state)
        ? existingAddress.state
        : kycResult.personalAddress.state,
      postalCode: completeExistingPostalCode ?? kycResult.personalAddress.postalCode,
      country: existingAddress?.country || 'US',
    };
  }

  if (hasValue(kycResult.phoneNumber) && !hasValue(userInfo.phoneNumber)) {
    prefill.phoneNumber = kycResult.phoneNumber;
  }

  return prefill;
}

export function hasCompleteApexPersonalInfoForKycSkip(userInfo: ApexUserInformation): boolean {
  const address = userInfo.personalAddress;

  return (
    hasValue(userInfo.firstName) &&
    hasValue(userInfo.lastName) &&
    hasValue(userInfo.phoneNumber) &&
    userInfo.dateOfBirth !== undefined &&
    hasCompleteAddress(address)
  );
}
